import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ClassActionStatus, getClassActionStatusTranslationKey, LawsuitCategory, ClassActionPublicView, PaginatedResponse } from '@tvia-ai/shared';
import { useAuthStore } from '../stores/authStore';
import { useLanguageStore } from '../stores/languageStore';
import { Link } from 'react-router-dom';

type ClassActionsResponse = PaginatedResponse<ClassActionPublicView>;

const JOIN_ERROR_KEYS: Record<string, string> = {
  'Lawsuit not found': 'classActions.errors.notFound',
  'This is not a class action lawsuit': 'classActions.errors.notClassAction',
  'Cannot join your own lawsuit': 'classActions.errors.cannotJoinOwn',
  'Already joined this lawsuit': 'classActions.errors.alreadyJoined',
};

const CATEGORIES: LawsuitCategory[] = [
  'consumer_rights',
  'employment',
  'personal_injury',
  'contract_dispute',
  'property',
  'family',
  'criminal',
  'other',
];

const STATUSES = [
  ClassActionStatus.Active,
  ClassActionStatus.Pending,
  ClassActionStatus.Settled,
];

const getStatusColor = (status: string) => {
  switch (status) {
    case ClassActionStatus.Active:
    case 'in_progress':
    case 'submitted':
      return 'bg-green-100 text-green-800';
    case ClassActionStatus.Pending:
    case 'analyzing':
    case 'questions_ready':
      return 'bg-yellow-100 text-yellow-800';
    case ClassActionStatus.Settled:
    case 'won':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const ClassActionsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { isRTL } = useLanguageStore();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sort, setSort] = useState<'createdAt' | 'participantCount'>('createdAt');
  const [page, setPage] = useState(1);
  const [joinSuccess, setJoinSuccess] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  const debounceTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }, []);

  const { data, isLoading, isError } = useQuery<ClassActionsResponse>({
    queryKey: ['class-actions', debouncedSearch, selectedCategory, selectedStatus, sort, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12',
        sort,
        sortOrder: 'desc',
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (selectedCategory) params.set('category', selectedCategory);
      if (selectedStatus) params.set('status', selectedStatus);

      const response = await axios.get(`/api/class-actions?${params.toString()}`);
      return response.data;
    },
    staleTime: 30000,
  });

  const joinMutation = useMutation({
    mutationFn: async (lawsuitId: string) => {
      const response = await axios.post(`/api/lawsuits/${lawsuitId}/join`);
      return response.data;
    },
    onSuccess: (_data, lawsuitId) => {
      setJoinSuccess(lawsuitId);
      setJoinError(null);
      queryClient.invalidateQueries({ queryKey: ['class-actions'] });
      setTimeout(() => setJoinSuccess(null), 4000);
    },
    onError: (error: any) => {
      const rawError = error?.response?.data?.error;
      const translationKey = rawError ? JOIN_ERROR_KEYS[rawError] : undefined;
      const msg = translationKey ? t(translationKey) : t('classActions.joinError');
      setJoinError(msg);
      setTimeout(() => setJoinError(null), 4000);
    },
  });

  const handleJoin = (lawsuitId: string) => {
    if (!user) {
      navigate('/login');
      return;
    }
    joinMutation.mutate(lawsuitId);
  };

  const classActions = data?.data || [];
  const pagination = data?.pagination;
  const totalPages = pagination?.pages || 1;

  if (isLoading) {
    return (
      <div className={`min-h-screen bg-gray-50 py-12 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8 mx-auto"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-lg shadow p-6">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-full mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 py-12 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('classActions.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('classActions.subtitle')}
          </p>
        </div>

        {/* Toast messages */}
        {joinSuccess && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
            {t('classActions.joinSuccess')}
          </div>
        )}
        {joinError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            {joinError}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('classActions.search')}
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('classActions.searchPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="class-actions-search"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('classActions.category')}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="class-actions-category-filter"
              >
                <option value="">{t('classActions.allCategories')}</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>
                    {t(`lawsuit.categories.${cat}`, cat)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('classActions.statusLabel')}
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="class-actions-status-filter"
              >
                <option value="">{t('classActions.allStatuses')}</option>
                {STATUSES.map(s => (
                  <option key={s} value={s}>
                    {t(getClassActionStatusTranslationKey(s), s)}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('classActions.sortBy')}
              </label>
              <select
                value={sort}
                onChange={(e) => { setSort(e.target.value as typeof sort); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="class-actions-sort"
              >
                <option value="createdAt">{t('classActions.sortNewest')}</option>
                <option value="participantCount">{t('classActions.sortMostJoined')}</option>
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-500">
            {pagination && t('classActions.results', { count: pagination.total })}
          </div>
        </div>

        {/* Error state */}
        {isError && (
          <div className="text-center py-12">
            <p className="text-red-600">{t('classActions.loadError', 'Failed to load class actions. Please try again.')}</p>
          </div>
        )}

        {/* Class Actions Grid */}
        {!isError && classActions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {t('classActions.noResults')}
            </h3>
            <p className="text-gray-600">
              {t('classActions.noResultsDescription')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classActions.map((action) => (
              <div
                key={action.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                data-testid="class-action-card"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                        {action.targetCompany}
                      </h3>
                      <span className="inline-block text-xs text-gray-500 uppercase tracking-wide">
                        {t(`lawsuit.categories.${action.category}`, action.category)}
                      </span>
                    </div>
                    <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusColor(action.status)}`}>
                      {t(getClassActionStatusTranslationKey(action.status as ClassActionStatus), action.status)}
                    </span>
                  </div>

                  {/* Summary */}
                  {action.publicSummary && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {action.publicSummary}
                    </p>
                  )}

                  {/* Details */}
                  <div className="space-y-1 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('classActions.participants')}:</span>
                      <span className="text-gray-900 font-medium">{action.participantCount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('classActions.dateFiled')}:</span>
                      <span className="text-gray-900">
                        {new Date(action.dateFiled).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('classActions.country')}:</span>
                      <span className="text-gray-900">{action.country}</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  {action.hasJoined ? (
                    <div
                      className="w-full bg-gray-100 text-gray-600 py-2 px-4 rounded-md text-center text-sm font-medium"
                      data-testid="already-joined-badge"
                    >
                      {t('classActions.pendingToJoin')}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleJoin(action.id)}
                      disabled={joinMutation.isPending && joinMutation.variables === action.id}
                      className="w-full bg-cyan-600 text-white py-2 px-4 rounded-md hover:bg-cyan-700 disabled:opacity-50 transition-colors duration-200 text-sm font-medium"
                      data-testid="join-lawsuit-btn"
                    >
                      {joinMutation.isPending && joinMutation.variables === action.id
                        ? t('common.loading', 'Loading...')
                        : t('classActions.requestToJoin')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination — always LTR so Prev/Next arrows stay semantically correct */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center items-center gap-3" dir="ltr" data-testid="pagination">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('classActions.prevPage')}
            </button>
            <span className="text-sm text-gray-600">
              {t('classActions.pageOf', { current: page, total: totalPages })}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('classActions.nextPage')}
            </button>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {t('classActions.ctaTitle')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('classActions.ctaDescription')}
          </p>
          <Link
            to="/file-lawsuit"
            className="bg-white text-cyan-600 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors duration-200"
          >
            {t('classActions.startNewLawsuit')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClassActionsPage;
